import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../App";
import { formatRuPhone, isValidRuPhone, RU_PHONE_PLACEHOLDER } from "../../utils/phone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TZ8-12: «Способы получения выплат» — СБП-телефон, история выплат, ссылка на реквизиты (подсистема карт удалена)
export const PaymentCardsSection = ({ user, showToast, setCurrentView }) => {
  const { fetchUser } = useContext(AuthContext);
  // Состояние для СБП телефона (контролируемый ввод)
  const [sbpPhone, setSbpPhone] = useState(formatRuPhone(user.sbp_phone || ''));

  // Новое состояние для выплат
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  // Проверяем что пользователь - исполнитель
  if (user.role !== "executor") {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Доступ ограничен</h3>
          <p className="text-yellow-700">
            Способы получения выплат доступны только исполнителям.
          </p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      setPayoutsLoading(true);
      const response = await axios.get(`${API}/profile/payouts`);
      setPayouts(response.data.payouts || []);
    } catch (error) {
      console.error("Error fetching payouts:", error);
      // Не показываем ошибку пользователю, если выплат просто нет
      setPayouts([]);
    } finally {
      setPayoutsLoading(false);
    }
  };

    return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}} data-testid="payout-methods-title">Способы получения выплат</h2>
        <p style={{color:'var(--rv-text-muted)'}}>Номер для СБП, реквизиты счёта и история выплат за выполненные задания</p>
      </div>

      {/* Информационный блок */}
      <div className="reversia-card p-4 mb-6" data-testid="payout-how-it-works" style={{background:'rgba(13,148,136,0.04)', borderColor:'rgba(13,148,136,0.15)'}}>
        <h3 className="font-medium mb-2" style={{color:'var(--rv-text-primary)'}}>Как работают выплаты</h3>
        <ul className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
          <li>• Выплаты происходят после подтверждения задания заказчиком</li>
          <li>• Комиссия платформы вычитается перед выплатой (10–25% в зависимости от типа задания)</li>
          <li>• Минимальная сумма выплаты: 100₽</li>
          <li>• Выплаты производятся через СБП по номеру телефона или на банковский счёт по реквизитам</li>
          <li>• Данные банковских карт платформа не запрашивает и не хранит</li>
        </ul>
      </div>
      
      {/* Блок для СБП номера */}
      <div className="reversia-card p-6 mb-6">
        <h3 className="font-semibold mb-1" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Номер телефона для СБП</h3>
        <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
          Укажите номер телефона для получения выплат через Систему Быстрых Платежей (СБП)
        </p>
        <div className="flex flex-col gap-2">
          <input
            type="tel"
            placeholder={RU_PHONE_PLACEHOLDER}
            inputMode="tel"
            maxLength={18}
            className="w-full px-3 py-2 rounded-md text-sm"
            style={{border:'1px solid var(--rv-border)', outline:'none', fontFamily:'IBM Plex Sans,sans-serif'}}
            value={sbpPhone}
            onChange={(e) => setSbpPhone(formatRuPhone(e.target.value))}
            data-testid="sbp-phone-input"
            onBlur={async (e) => {
              const newVal = e.target.value;
              if (newVal && !isValidRuPhone(newVal)) {
                showToast("Введите номер в формате +7 (999) 123-45-67", "error");
                return;
              }
              if (newVal !== (user.sbp_phone || '')) {
                try {
                  const response = await axios.put(`${API}/profile/executor-details`, { sbp_phone: newVal });
                  if (response.data.success) {
                    showToast("Номер СБП обновлён", "success");
                    // Обновляем глобальный объект user, чтобы номер не "терялся"
                    // при повторном открытии вкладки (компонент размонтируется/монтируется заново)
                    if (fetchUser) await fetchUser();
                  }
                } catch (error) {
                  showToast("Ошибка обновления номера СБП", "error");
                }
              }
            }}
          />
          <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>Необходимо для ручных выплат</p>
        </div>
      </div>

      {/* История выплат */}
      <div className="reversia-card p-6 mb-6">
        <h3 className="font-semibold mb-3" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>История выплат</h3>
        {payoutsLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
            <p className="mt-2 text-sm" style={{color:'var(--rv-text-muted)'}}>Загружаем выплаты...</p>
          </div>
        ) : payouts.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Выплат пока нет</p>
            <p className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>Выплаты появятся после подтверждения заданий заказчиками</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payouts.slice(0, 5).map((payout, index) => (
              <div key={index} className="p-3 rounded-lg" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm" style={{color:'var(--rv-text-primary)'}}>{payout.task_title}</h4>
                    <p className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                      {payout.original_amount}₽ → Выплата: <span className="font-semibold" style={{color:'#10b981'}}>{payout.payout_amount}₽</span>
                    </p>
                    <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                      {payout.confirmed_at ? new Date(payout.confirmed_at).toLocaleDateString('ru-RU') : '—'}
                    </p>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{
                      background: (payout.payout_status === 'succeeded' || payout.payout_status === 'paid_manually') ? 'rgba(16,185,129,0.1)' :
                                  (payout.payout_status === 'pending' || payout.payout_status === 'awaiting_manual_payout') ? 'rgba(217,119,6,0.1)' :
                                  'rgba(107,114,128,0.1)',
                      color: (payout.payout_status === 'succeeded' || payout.payout_status === 'paid_manually') ? '#10b981' :
                             (payout.payout_status === 'pending' || payout.payout_status === 'awaiting_manual_payout') ? '#d97706' :
                             'var(--rv-text-muted)',
                    }}>
                      {payout.payout_status === 'succeeded' ? 'Авто' :
                       payout.payout_status === 'paid_manually' ? 'Вручную' :
                       payout.payout_status === 'pending' ? 'В обработке' : 
                       payout.payout_status === 'awaiting_manual_payout' ? 'Ожидает' :
                       payout.payout_status || 'Неизвестно'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {payouts.length > 5 && (
              <p className="text-center text-xs" style={{color:'var(--rv-text-muted)'}}>
                Показано 5 из {payouts.length} выплат
              </p>
            )}
          </div>
        )}
      </div>

      {/* Реквизиты счёта — в разделе «Бизнес-статус» (T4-2) */}
      <div className="reversia-card p-6 mb-6" data-testid="payout-bank-details-link-block">
        <h3 className="font-semibold mb-1" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>Банковские реквизиты</h3>
        <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
          Реквизиты расчётного счёта (БИК, р/с) указываются в разделе «Бизнес-статус» и используются для оплаты по счёту.
        </p>
        <button
          data-testid="payout-bank-details-link"
          onClick={() => setCurrentView && setCurrentView("business-status")}
          className="reversia-btn reversia-btn-teal reversia-btn-md"
        >
          Перейти к реквизитам
        </button>
      </div>
    </div>
  );
};
